/**
 * Consolidated transaction type label mappings for statement generation.
 *
 * Used by: blockchainStatementPdfTemplate, fiatStatementPdfTemplate, generateStatementCsv
 *
 * Keys are composite "journal:reason" strings. The wildcard "journal:*" is used
 * as a fallback when no specific reason match is found for a given journal.
 */
/**
 * The label every fee `journal:reason` pair maps to. Both statement exports key
 * their "this is a fee, don't name a counterparty" guards off this value, so it
 * must not be inlined as a literal at those call sites — renaming the label
 * would otherwise silently re-attribute every fee row to the payment recipient.
 */
export const TRANSACTION_FEE_LABEL = "Transaction fee";

export const TRANSACTION_TYPE_LABELS: Record<string, string> = {
  // Payment labels
  "payments:cancellation": "Payment cancelled",
  "payments:cancelled-payment": "Payment cancelled",
  "payments:payment-reversal": "Payment returned",
  "payments:pending-payment": "Payment pending",
  "payments:fee": TRANSACTION_FEE_LABEL,
  "payments:*": "Payment",

  // Bank transaction labels
  "bank-transactions:credit": "Deposit",
  "bank-transactions:debit": "Payment",
  "bank-transactions:credit-deduction": TRANSACTION_FEE_LABEL,
  "bank-transactions:debit-deduction": TRANSACTION_FEE_LABEL,
  "bank-transactions:credit-rebate": "Credit rebate",
  "bank-transactions:debit-rebate": "Debit rebate",
  "bank-transactions:*": "Bank Transfer",

  // Blockchain transaction labels
  "blockchain-transactions:credit": "Deposit",
  "blockchain-transactions:debit": "Payment",
  "blockchain-transactions:fee": TRANSACTION_FEE_LABEL,
  "blockchain-transactions:credit-deduction": TRANSACTION_FEE_LABEL,
  "blockchain-transactions:debit-deduction": TRANSACTION_FEE_LABEL,
  "blockchain-transactions:credit-rebate": "Credit rebate",
  "blockchain-transactions:debit-rebate": "Debit rebate",
  "blockchain-transactions:*": "Transaction",

  // Blockchain withdrawal labels
  "blockchain-withdrawals:withdrawal-reversal": "Payment returned",
  "blockchain-withdrawals:cancelled-withdrawal": "Payment cancelled",
  "blockchain-withdrawals:fee": TRANSACTION_FEE_LABEL,
  "blockchain-withdrawals:*": "Payment",

  // FX ticket labels
  "fx-tickets:settlement": "Currency exchange",
  "fx-tickets:*": "Currency exchange",

  // Posting labels
  "postings:*": "Other",
};

export function getTransactionTypeLabel(
  journal?: string,
  reason?: string,
  resolvedData?: { paymentState?: string }
): string {
  if (!journal) return "Transaction";

  // Special case: cancelled payment state overrides normal label
  if (journal === "payments" && resolvedData?.paymentState === "CANCELLED") {
    if (reason === "pending-payment") return "Payment cancelled";
    if (reason === "cancellation") return "Payment returned";
  }

  const key = `${journal}:${reason}`;
  const wildcardKey = `${journal}:*`;

  return (
    TRANSACTION_TYPE_LABELS[key] ||
    TRANSACTION_TYPE_LABELS[wildcardKey] ||
    journal
  );
}
